import Field from "@ui/Form/Field";
import t from "../../../../i18n";
import TextareaInput from "@ui/TextareaInput";
import Text from "@ui/Text";
import { MAX_FORMULA_LENGTH } from "../../../../domain/parser/quantity/csv/rules";
import useInputAsyncValidation from "./hook/useInputAsyncValidation";
import React, { useMemo, useState } from "react";
import { usePost } from "../../../../api/useCall";
import { colors, spacings } from "@ui/Theme";
import { css } from "@linaria/core";
import { DateTime } from "luxon";
import { ArrowDownSLine, ArrowRightSLine, DeleteBinLine } from "@ui/IconV2/react";
import { DatePicker } from "@ui/DatePicker";
import { parseAbsolute } from "@internationalized/date";
import { useUserCanEdit } from "../../Dashboards/hook/useUserCanEdit";
import ButtonIconV2 from "@ui/ButtonIconV2";
import type { DateValue } from "react-aria";
import formatDate from "@ui/i18n/formatDate";
import {
  FillStartDateFromCycleOrSetup,
  type DateSourceOption,
} from "./FillStartDateFromCycleOrSetup";

export const FormulaField = ({
  versionId,
  quantityId,
  projectId,
  timezone,
  index,
  formula,
  startsAt,
  endsAt,
  legacyFormula,
  onFormulaChange,
  onStartsAtChange,
  onDelete,
  onValidationErrorChange,
  onValidationPendingChange,
  disabled,
  deleteDisabled,
  startsAtError,
  cycleOptions = [],
  setupOptions = [],
  onStartsAtFillFromCycle,
  onStartsAtFillFromSetup,
  required = false,
}: {
  versionId: string | null;
  quantityId: string | null;
  projectId: string;
  timezone: string;
  formula: string;
  index: number;
  startsAt: Date | null;
  endsAt: Date | null;
  legacyFormula?: boolean;
  onFormulaChange: (versionId: string, value: string) => void;
  onStartsAtChange?: (versionId: string, value: Date | null) => void;
  onDelete?: (versionId: string) => void;
  onValidationErrorChange?: (versionId: string | null, error?: string) => void;
  onValidationPendingChange?: (versionId: string | null, isValidating?: boolean) => void;
  disabled?: boolean;
  deleteDisabled?: boolean;
  startsAtError?: string;
  cycleOptions?: DateSourceOption[];
  setupOptions?: DateSourceOption[];
  onStartsAtFillFromCycle?: (versionId: string, startsAt: Date) => void;
  onStartsAtFillFromSetup?: (versionId: string, startsAt: Date) => void;
  required?: boolean;
}) => {
  const userCanEdit = useUserCanEdit(projectId);
  const [open, setOpen] = useState<boolean>(true);
  const isReadOnly = disabled || !userCanEdit;
  const validateFormula = usePost("/projects/:projectId(uuid)/quantities/formula/validate");
  const runAsyncValidation = React.useCallback(
    async (input: string) => {
      const { status, body } = await validateFormula({
        body: {
          id: quantityId,
          formula: input,
        },
        routeParams: { projectId },
      });
      if (status === 417) return body;
      else if (status === 204) return;
      else throw new Error(status);
    },
    [projectId, quantityId, validateFormula],
  );
  const asyncValidation = useInputAsyncValidation({
    defaultValue: formula,
    validateOnBlur: true,
    asyncValidation: runAsyncValidation,
  });

  const from = useMemo(
    () => startsAt && DateTime.fromJSDate(startsAt, { zone: timezone }),
    [startsAt, timezone],
  );

  const to = useMemo(
    () => endsAt && DateTime.fromJSDate(endsAt, { zone: timezone }),
    [endsAt, timezone],
  );

  const startsAtValue = React.useMemo(
    () => (from && from.isValid ? parseAbsolute(from.toISO(), timezone) : null),
    [from, timezone],
  );

  const handleDateChange = React.useCallback(
    (value: DateValue | null) => {
      if (!onStartsAtChange || value === null || versionId === null) return;
      onStartsAtChange(versionId, value.toDate(timezone));
    },
    [versionId, onStartsAtChange, timezone],
  );

  const handleCycleChange = React.useCallback(
    (option: DateSourceOption | null) => {
      if (!onStartsAtFillFromCycle || versionId === null || option === null) return;
      onStartsAtFillFromCycle(versionId, option.startsAt);
    },
    [onStartsAtFillFromCycle, versionId],
  );

  const handleSetupChange = React.useCallback(
    (option: DateSourceOption | null) => {
      if (!onStartsAtFillFromSetup || versionId === null || option === null) return;
      onStartsAtFillFromSetup(versionId, option.startsAt);
    },
    [onStartsAtFillFromSetup, versionId],
  );
  React.useEffect(() => {
    onValidationErrorChange?.(versionId, isReadOnly ? undefined : asyncValidation.error);
    return () => {
      onValidationErrorChange?.(versionId);
    };
  }, [asyncValidation.error, isReadOnly, onValidationErrorChange, versionId]);

  React.useEffect(() => {
    onValidationPendingChange?.(
      versionId,
      isReadOnly ? undefined : asyncValidation.hasPendingValidation || asyncValidation.isValidating,
    );
    return () => {
      onValidationPendingChange?.(versionId);
    };
  }, [
    asyncValidation.hasPendingValidation,
    asyncValidation.isValidating,
    isReadOnly,
    onValidationPendingChange,
    versionId,
  ]);

  return (
    <div>
      <header className={formulaFieldAccordionHeader}>
        <button className={formulaFieldAccordToggle} onClick={() => setOpen(s => !s)}>
          {open ? <ArrowDownSLine /> : <ArrowRightSLine />}
          <Text type="body1SemiBold" as="span">
            {t("project.settings.quantity.side_panel.section.formula_input.input_label")} {index}
          </Text>
          {(from || to) && "—"}
          <Text type="body1" as="span">
            {t(
              "project.settings.quantity.side_panel.section.formula_input.usage_period",
              from ? formatDate(from.toJSDate(), "slash", timezone) : null,
              to ? formatDate(to.toJSDate(), "slash", timezone) : null,
            )}
          </Text>
        </button>
        {!legacyFormula && versionId && (
          <ButtonIconV2
            onClick={() => onDelete?.(versionId)}
            disabled={deleteDisabled || !userCanEdit || onDelete === undefined}
          >
            <DeleteBinLine color={colors.oxygen500} />
          </ButtonIconV2>
        )}
      </header>
      {open && (
        <div className={formulaFieldContainer}>
          <div>
            {from && from.isValid && (
              <Field
                id=""
                label={t(
                  "project.settings.quantity.side_panel.section.formula_input.usage_period_label",
                )}
                className={formulaField}
                disabled={isReadOnly}
                error={startsAtError}
              >
                <DatePicker
                  id=""
                  disabled={isReadOnly}
                  isNonModal
                  dateOptions={{
                    value: startsAtValue,
                    onChange: handleDateChange,
                  }}
                />
              </Field>
            )}
            {!legacyFormula && versionId !== null && (
              <FillStartDateFromCycleOrSetup
                cycleOptions={cycleOptions}
                setupOptions={setupOptions}
                disabled={isReadOnly}
                onCycleSelect={option => handleCycleChange(option)}
                onSetupSelect={option => handleSetupChange(option)}
              />
            )}
          </div>
          <Field
            id=""
            label={t("project.settings.quantity.side_panel.section.formula_input.input_label")}
            className={formulaField}
            disabled={isReadOnly}
            required={required}
          >
            <TextareaInput
              value={formula}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                if (isReadOnly) return;
                asyncValidation.handleChange(e);
                onFormulaChange(versionId ?? "", e.target.value);
              }}
              onBlur={isReadOnly ? undefined : asyncValidation.handleBlur}
              onFocus={isReadOnly ? undefined : asyncValidation.handleFocus}
              error={isReadOnly ? undefined : asyncValidation.error}
              maxLength={MAX_FORMULA_LENGTH}
              minHeight={79}
              enforceMaxLength={false}
              showCharCounter={false}
              textareaClassName={disabledTextareaStyle}
            />
          </Field>
        </div>
      )}{" "}
    </div>
  );
};

const formulaFieldAccordionHeader = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacings.xs};
`;

const formulaFieldAccordToggle = css`
  all: unset;
  display: flex;
  align-items: center;
  column-gap: ${spacings.xxs};
  color: ${colors.carbon900};
`;

const formulaFieldContainer = css`
  display: flex;
  flex-direction: column;
  gap: ${spacings.m};
`;

const formulaField = css`
  width: 100%;

  > div {
    border: unset;
    padding-left: 0;
    width: 100%;

    > div {
      width: 100%;

      div[class*="control"] {
        border-color: ${colors.titanium200};
      }
    }
  }
`;

const disabledTextareaStyle = css`
  &:disabled {
    color: ${colors.titanium200} !important;
  }
`;
